import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { useIsMobile } from '../ui/use-mobile';
import { Plus, Edit, Trash2, Loader2, Search } from 'lucide-react';
import { Course } from '../../types';
import { api } from '../../utils/api';
import { normalizeAcademicYearList } from '../../utils/academicYear';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function CourseManagement() {
  const { accessToken } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    department: '',
    units: 0,
    curriculumVersionsInput: ''
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      const data = await api.getCourses(token);
      setCourses(data.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const token = accessToken || '';
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        department: formData.department,
        units: formData.units,
        curriculumVersions: normalizeAcademicYearList(formData.curriculumVersionsInput)
      };
      
      if (editingCourse) {
        await api.updateCourse(editingCourse.id, payload, token);
        toast.success('Course updated successfully');
      } else {
        await api.createCourse(payload, token);
        toast.success('Course created successfully');
      }
      
      await fetchCourses();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save course');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    setFormData({
      code: course.code,
      name: course.name,
      description: course.description,
      department: course.department,
      units: course.units,
      curriculumVersionsInput: (course.curriculumVersions || []).join('\n')
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (courseId: string) => {
    if (!confirm('Are you sure you want to delete this course?')) {
      return;
    }
    
    try {
      const token = accessToken || '';
      await api.deleteCourse(courseId, token);
      toast.success('Course deleted successfully');
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingCourse(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      department: '',
      units: 0,
      curriculumVersionsInput: ''
    });
  };

  const filteredCourses = courses.filter((course) => {
    const matchesCode = !filterCode || course.code.toLowerCase().includes(filterCode.toLowerCase());
    const matchesName = !filterName || course.name.toLowerCase().includes(filterName.toLowerCase());
    return matchesCode && matchesName;
  });

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / rowsPerPage));
  const pageStartIndex = (currentPage - 1) * rowsPerPage;
  const paginatedCourses = filteredCourses.slice(pageStartIndex, pageStartIndex + rowsPerPage);
  const paginationStart = filteredCourses.length === 0 ? 0 : pageStartIndex + 1;
  const paginationEnd = filteredCourses.length === 0
    ? 0
    : Math.min(pageStartIndex + rowsPerPage, filteredCourses.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCode, filterName, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }

    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
  };

  const visiblePages = getVisiblePages();

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <CardTitle>Program Management</CardTitle>
            <CardDescription className="mt-1">Manage academic programs</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size={isMobile ? "sm" : "default"}>
                <Plus className="mr-2 h-4 w-4" />
                {isMobile ? "Add" : "Add Program"}
              </Button>
            </DialogTrigger>
            <DialogContent className={isMobile ? "max-w-[95vw] max-h-[90vh] overflow-y-auto" : "max-w-2xl max-h-[90vh] overflow-y-auto"}>
              <DialogHeader>
                <DialogTitle>
                  {editingCourse ? 'Edit Program' : 'Add New Program'}
                </DialogTitle>
                <DialogDescription>
                  {editingCourse 
                    ? 'Update the program information below' 
                    : 'Fill in the details to create a new academic program'
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Program Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value})}
                      placeholder="e.g., BSIT"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="units">Total Units</Label>
                    <Input
                      id="units"
                      type="number"
                      value={formData.units}
                      onChange={(e) => setFormData({...formData, units: parseInt(e.target.value)})}
                      placeholder="e.g., 140"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Program Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Master of Science in Computer Science"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    placeholder="e.g., Department of Computer Science"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Program description..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="curriculumVersions">Curriculum Versions</Label>
                  <Textarea
                    id="curriculumVersions"
                    value={formData.curriculumVersionsInput}
                    onChange={(e) => setFormData({...formData, curriculumVersionsInput: e.target.value})}
                    placeholder="One academic year per line, e.g.&#10;2019-2020&#10;2026-2027"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Use academic years to distinguish curriculum versions for the same program.
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingCourse ? 'Update' : 'Add'} Program
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter by program code..."
              value={filterCode}
              onChange={(e) => setFilterCode(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter by program name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            {courses.length === 0
              ? 'No programs found. Add your first program to get started.'
              : 'No programs match the current filters.'}
          </div>
        ) : (
          <>
            <ScrollArea className="w-full" type="auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[100px]">Code</TableHead>
                    <TableHead className="min-w-[200px]">Name</TableHead>
                    <TableHead className="min-w-[180px]">Curriculum Versions</TableHead>
                    <TableHead className="min-w-[80px]">Units</TableHead>
                    <TableHead className="min-w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCourses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell>
                        <Badge variant="outline">{course.code}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{course.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(course.curriculumVersions || []).length > 0 ? (
                            (course.curriculumVersions || []).map((version) => (
                              <Badge key={version} variant="secondary">{version}</Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">Not versioned</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{course.units} units</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(course)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(course.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {paginationStart}-{paginationEnd} of {filteredCourses.length} programs
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rows per page</Label>
                  <Select value={String(rowsPerPage)} onValueChange={(value) => setRowsPerPage(Number(value))}>
                    <SelectTrigger className="w-[88px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Pagination className="justify-end sm:justify-center">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    {visiblePages.map((page, index) => {
                      const previousPage = visiblePages[index - 1];
                      const shouldShowEllipsis = previousPage !== undefined && page - previousPage > 1;

                      return (
                        <div key={page} className="flex items-center">
                          {shouldShowEllipsis && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              href="#"
                              isActive={page === currentPage}
                              onClick={(event) => {
                                event.preventDefault();
                                handlePageChange(page);
                              }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </div>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
